import React, { useEffect, useRef, useState } from 'react';
import { FiMoreHorizontal, FiShare2, FiDownload, FiImage, FiType, FiCornerUpLeft, FiCornerUpRight } from 'react-icons/fi';
import './MapActionsMenu.css';

export default function MapActionsMenu({ actions, isDarkMode, isVisible = true }) {
  const [open, setOpen] = useState(false);
  const wrapper = useRef(null);
  const trigger = useRef(null);
  const panel = useRef(null);
  const close = (restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus) trigger.current?.focus();
  };
  useEffect(() => { if (!isVisible) setOpen(false); }, [isVisible]);
  useEffect(() => {
    if (!open) return undefined;
    panel.current?.querySelector('[role="menuitem"]:not(:disabled)')?.focus();
    const outside = event => {
      if (!wrapper.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);
  if (!actions?.mapActions) return null;
  const map = actions.mapActions;
  const items = [
    { label: 'Undo', icon: FiCornerUpLeft, run: map.undo, disabled: !map.canUndo?.(), shortcut: 'Ctrl/Cmd+Z' },
    { label: 'Redo', icon: FiCornerUpRight, run: map.redo, disabled: !map.canRedo?.(), shortcut: 'Ctrl/Cmd+Shift+Z' },
    { label: 'Map font', icon: FiType, run: map.font },
    { label: 'Share map', icon: FiShare2, run: map.share },
    { label: 'Export image', icon: FiImage, run: map.exportImage },
    { label: 'Export map', icon: FiDownload, run: actions.exportCurrentMap },
  ].filter(item => typeof item.run === 'function');
  return <div ref={wrapper} className="map-actions" data-dark={isDarkMode} data-export-ignore="true"
    onPointerDown={event => event.stopPropagation()} onKeyDown={event => {
      event.stopPropagation();
      if (event.key === 'Escape') { event.preventDefault(); close(true); }
    }}>
    <button ref={trigger} type="button" className="map-actions__trigger" aria-label="Map actions" title="Map actions"
      aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(value => !value)}
      onKeyDown={event => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); setOpen(true); }
      }}><FiMoreHorizontal aria-hidden="true" size={18} /></button>
    {open && <div ref={panel} role="menu" aria-label="Map actions" className="map-actions__panel" data-viewport-occlusion="true"
      onKeyDown={event => {
        if (event.key === 'Tab') { close(true); return; }
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const buttons = [...panel.current.querySelectorAll('[role="menuitem"]:not(:disabled)')];
        const current = buttons.indexOf(document.activeElement);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1
          : (current + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
        buttons[next]?.focus();
      }}>
      {items.map(({ label, icon: Icon, run, disabled, shortcut }) => <button key={label} type="button" role="menuitem" disabled={disabled}
        title={shortcut ? `${label} (${shortcut})` : label} onClick={() => {
          // Close first, returning focus to a stable anchor before a dialog opens.
          close(true);
          run();
        }}><Icon aria-hidden="true" size={17} /><span>{label}</span></button>)}
    </div>}
  </div>;
}
